import { getBackendUrl } from '../../config/environment';
/**
 * AdminModules - Page d'administration complète
 * Permet de gérer TOUS les Piliers ET tous les contenus des Modules
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  Save, 
  X, 
  ArrowLeft, 
  Layers,
  BookOpen,
  Eye,
  EyeOff,
  GripVertical,
  FileText,
  Users,
  Sparkles,
  DollarSign,
  Share2,
  MessageSquare,
  Award,
  Shield,
  ShieldCheck,
  Volume2,
  Loader2,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Clock,
  ShoppingCart,
  Building2,
  Target,
  Mail,
  Activity
} from 'lucide-react';
import UserManagement from '../../components/UserManagement';
import PricingManagement from '../../components/PricingManagement';
import AffiliationManagement from '../../components/AffiliationManagement';
import AffiliateLevelsManagement from '../../components/admin/AffiliateLevelsManagement';
import ScenarioGenerator from '../../components/ScenarioGenerator';
import TestimonialsManagement from '../../components/TestimonialsManagement';
import AdminCategoryNav from '../../components/admin/AdminCategoryNav';

const BACKEND_URL = window.location.hostname === 'localhost' 
  ? 'http://localhost:8001' 
  : getBackendUrl();
const API = `${BACKEND_URL}/api`;

const AdminModules = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // States
  const [activeTab, setActiveTab] = useState('pillars');
  const [pillars, setPillars] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(''); // 'pillar' ou 'module'
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});

  // Authentication check
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadData();
  }, [user, navigate]);

  // Load all data
  const loadData = async () => {
    setLoading(true);
    try {
      // Load pillars
      const pillarsResponse = await axios.get(`${API}/pillars`);
      setPillars(pillarsResponse.data || []);

      // Load modules
      const modulesResponse = await axios.get(`${API}/modules-v2`);
      setModules(modulesResponse.data || []);
    } catch (error) {
      console.error('Erreur chargement:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  // ==========================
  // PILLAR MANAGEMENT
  // ==========================

  const handleOpenPillarDialog = (pillar = null) => {
    setEditMode('pillar');
    setEditingItem(pillar);
    setFormData(pillar ? {
      id: pillar.id,
      name: pillar.name || pillar.title,
      description: pillar.description,
      icon: pillar.icon || '',
      order: pillar.order
    } : {
      name: '',
      description: '',
      icon: '',
      order: pillars.length + 1
    });
    setIsDialogOpen(true);
  };

  const handleSavePillar = async (e) => {
    e.preventDefault();
    
    try {
      const method = editingItem ? 'put' : 'post';
      const url = editingItem ? `${API}/pillars/${editingItem.id}` : `${API}/pillars`;
      
      const payload = {
        name: formData.name,
        description: formData.description,
        icon: formData.icon || '',
        order: parseInt(formData.order) || 1
      };

      await axios[method](url, payload);
      
      toast.success(editingItem ? 'Pilier modifié' : 'Pilier créé');
      setIsDialogOpen(false);
      await loadData();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de la sauvegarde du pilier');
    }
  };

  const handleDeletePillar = async (pillarId) => {
    if (!window.confirm('Supprimer ce pilier ?')) return;

    try {
      await axios.delete(`${API}/pillars/${pillarId}`);
      toast.success('Pilier supprimé');
      await loadData();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  // ==========================
  // MODULE MANAGEMENT
  // ==========================

  const handleOpenModuleDialog = (module = null) => {
    setEditMode('module');
    setEditingItem(module);
    setFormData(module ? {
      id: module.id,
      pillar_id: module.pillar_id,
      title: module.title,
      description: module.description,
      icon: module.icon || '',
      order: module.order,
      duration_minutes: module.duration_minutes || 30,
      is_visible: module.is_visible !== false
    } : {
      pillar_id: pillars[0]?.id || 1,
      title: '',
      description: '',
      icon: '',
      order: modules.length + 1,
      duration_minutes: 30,
      is_visible: true
    });
    setIsDialogOpen(true);
  };

  const handleSaveModule = async (e) => {
    e.preventDefault();
    
    try {
      const method = editingItem ? 'patch' : 'post';
      const url = editingItem ? `${API}/modules-v2/${editingItem.id}` : `${API}/modules-v2`;
      
      const payload = {
        pillar_id: parseInt(formData.pillar_id),
        title: formData.title,
        description: formData.description,
        icon: formData.icon || '',
        order: parseInt(formData.order) || 1,
        duration_minutes: parseInt(formData.duration_minutes) || 30,
        is_visible: formData.is_visible !== false
      };

      await axios[method](url, payload);
      
      toast.success(editingItem ? 'Module modifié' : 'Module créé');
      setIsDialogOpen(false);
      await loadData();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de la sauvegarde du module');
    }
  };

  const handleToggleModuleVisibility = async (moduleId, currentStatus) => {
    try {
      await axios.patch(`${API}/modules-v2/${moduleId}`, {
        is_visible: !currentStatus
      });
      
      toast.success('Visibilité modifiée');
      await loadData();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de la modification');
    }
  };

  const handleDeleteModule = async (moduleId) => {
    if (!window.confirm('Supprimer ce module ?')) return;

    try {
      await axios.delete(`${API}/modules-v2/${moduleId}`);
      toast.success('Module supprimé');
      await loadData();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // ==========================
  // RENDER
  // ==========================

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="flex items-center gap-3">
              <Button
                data-testid="back-to-training"
                onClick={() => navigate('/training')} 
                variant="ghost"
                size="sm"
                className="gap-1 px-2 sm:px-3 flex-shrink-0"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Retour au training</span>
                <span className="sm:hidden">Retour</span>
              </Button>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">Administration</h1>
                <p className="text-xs sm:text-sm text-gray-600 hidden sm:block">Gérer le contenu de la formation</p>
              </div>
            </div>
            <span className="text-xs sm:text-sm text-gray-500 truncate hidden sm:block">{user?.email}</span>
          </div>
        </div>
      </header>

      {/* Tabs — Navigation premium par catégories métier */}
      <AdminCategoryNav
        currentTab={activeTab}
        categories={[
          {
            id: 'pedagogie',
            label: 'Pédagogie & IA',
            icon: Layers,
            items: [
              { id: 'pillars', label: `Piliers (${pillars.length})`, icon: Layers, activeKey: 'pillars',
                onClick: () => setActiveTab('pillars'), testid: 'admin-cat-pillars' },
              { id: 'modules', label: `Modules (${modules.length})`, icon: BookOpen, activeKey: 'modules',
                onClick: () => setActiveTab('modules'), testid: 'admin-cat-modules' },
              { id: 'scenarios', label: 'Scénarios IA', icon: Sparkles, activeKey: 'scenarios',
                onClick: () => setActiveTab('scenarios'), testid: 'admin-cat-scenarios' },
              { id: 'themes', label: 'Thèmes Libre', icon: Sparkles,
                to: '/admin/themes', testid: 'admin-cat-themes' },
              { id: 'qualiopi', label: 'Qualiopi', icon: Shield,
                to: '/admin/pedagogical-governance', testid: 'admin-cat-qualiopi' },
            ],
          },
          {
            id: 'coachs',
            label: 'Coachs & Utilisateurs',
            icon: Users,
            items: [
              { id: 'users', label: 'Utilisateurs', icon: Users, activeKey: 'users',
                onClick: () => setActiveTab('users'), testid: 'admin-cat-users' },
              { id: 'coaches', label: 'Espace Coachs', icon: Users,
                to: '/admin/coaches', testid: 'admin-coaches-tab' },
              { id: 'testimonials', label: 'Témoignages', icon: MessageSquare, activeKey: 'testimonials',
                onClick: () => setActiveTab('testimonials'), testid: 'admin-cat-testimonials' },
            ],
          },
          {
            id: 'business',
            label: 'Business & Acquisition',
            icon: Building2,
            items: [
              { id: 'b2b-quotes', label: 'Devis B2B', icon: Building2,
                to: '/admin/enterprise-quotes', testid: 'admin-enterprise-quotes-tab' },
              { id: 'b2b-leads', label: 'B2B / Leads', icon: Mail,
                to: '/admin/b2b-leads', testid: 'admin-b2b-leads-tab' },
              { id: 'discovery-overview', label: 'Discovery Overview', icon: Target,
                to: '/admin/discovery-overview', testid: 'admin-discovery-overview-tab' },
              { id: 'enterprise-orders', label: 'Commandes & Facturation B2B', icon: ShoppingCart,
                to: '/admin/enterprise-orders', testid: 'admin-enterprise-orders-tab' },
            ],
          },
          {
            id: 'finance',
            label: 'Finance & Facturation',
            icon: FileText,
            items: [
              { id: 'pricing', label: 'Tarification', icon: FileText, activeKey: 'pricing',
                onClick: () => setActiveTab('pricing'), testid: 'admin-cat-pricing' },
              { id: 'invoices', label: 'Facturation B2C', icon: FileText,
                to: '/admin/invoices', testid: 'admin-invoices-tab' },
              { id: 'coach-payments', label: 'Paiements coachs', icon: FileText,
                to: '/admin/coach-payments', testid: 'admin-coach-payments-tab' },
            ],
          },
          {
            id: 'affiliation',
            label: 'Affiliations & Croissance',
            icon: Award,
            items: [
              { id: 'affiliation', label: 'Affiliation', icon: Users, activeKey: 'affiliation',
                onClick: () => setActiveTab('affiliation'), testid: 'admin-cat-affiliation' },
              { id: 'affiliate-levels', label: 'Niveaux Affiliés', icon: Award, activeKey: 'affiliate-levels',
                onClick: () => setActiveTab('affiliate-levels'), testid: 'admin-cat-affiliate-levels' },
            ],
          },
          {
            id: 'systeme',
            label: 'Système & Audit',
            icon: Activity,
            items: [
              { id: 'marketplace-settings', label: 'Paramètres Marketplace', icon: Shield,
                to: '/admin/marketplace-settings', testid: 'admin-marketplace-settings-tab' },
              { id: 'pending-verification', label: 'Sessions à vérifier', icon: Shield,
                to: '/admin/pending-verification', testid: 'admin-pending-verification-tab' },
              { id: 'marketplace-pricing', label: 'Pricing & Promotions', icon: Shield,
                to: '/admin/marketplace-pricing', testid: 'admin-marketplace-pricing-tab' },
              { id: 'compliance', label: 'Conformité RH', icon: ShieldCheck,
                to: '/admin/compliance', testid: 'admin-compliance-tab' },
              { id: 'coaching-context', label: 'Partage Owen', icon: Shield,
                to: '/admin/coaching-context', testid: 'admin-coaching-context-tab' },
              { id: 'demo-export', label: 'Export vidéo démo', icon: FileText,
                to: '/admin/demo-export', testid: 'admin-demo-export-tab' },
              { id: 'audit-log', label: 'Audit Log', icon: Activity,
                to: '/admin/audit-log', testid: 'admin-audit-log-tab' },
            ],
          },
        ]}
      />

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'pillars' ? (
          <PillarsTab
            pillars={pillars}
            onAdd={() => handleOpenPillarDialog()}
            onEdit={handleOpenPillarDialog}
            onDelete={handleDeletePillar}
          />
        ) : activeTab === 'users' ? (
          <UserManagement />
        ) : activeTab === 'pricing' ? (
          <PricingManagement />
        ) : activeTab === 'affiliation' ? (
          <AffiliationManagement />
        ) : activeTab === 'affiliate-levels' ? (
          <AffiliateLevelsManagement />
        ) : activeTab === 'scenarios' ? (
          <ScenarioGenerator />
        ) : activeTab === 'testimonials' ? (
          <TestimonialsManagement />
        ) : (
          <ModulesTab
            modules={modules}
            pillars={pillars}
            navigate={navigate}
            onAdd={() => handleOpenModuleDialog()}
            onEdit={handleOpenModuleDialog}
            onDelete={handleDeleteModule}
            onToggleVisibility={handleToggleModuleVisibility}
          />
        )}
      </main>

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editMode === 'pillar' 
                ? (editingItem ? 'Modifier le pilier' : 'Nouveau pilier')
                : (editingItem ? 'Modifier le module' : 'Nouveau module')
              }
            </DialogTitle>
            <DialogDescription>
              {editMode === 'pillar' 
                ? 'Définissez les informations du pilier de formation'
                : 'Définissez les informations du module de formation'
              }
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={editMode === 'pillar' ? handleSavePillar : handleSaveModule} className="space-y-4">
            {editMode === 'pillar' ? (
              // Pillar Form
              <>
                <div>
                  <Label htmlFor="name">Nom du pilier *</Label>
                  <Input
                    id="name"
                    value={formData.name || ''}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="ex: Posture & Fondamentaux du Leadership"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    value={formData.description || ''}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={4}
                    placeholder="Décrivez ce pilier..."
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="icon">Icône (emoji)</Label>
                    <Input
                      id="icon"
                      value={formData.icon || ''}
                      onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                      placeholder="📚"
                      maxLength={2}
                    />
                  </div>

                  <div>
                    <Label htmlFor="order">Ordre</Label>
                    <Input
                      id="order"
                      type="number"
                      value={formData.order || 1}
                      onChange={(e) => setFormData({ ...formData, order: e.target.value })}
                      min="1"
                      max="10"
                    />
                  </div>
                </div>
              </>
            ) : (
              // Module Form
              <>
                <div>
                  <Label htmlFor="pillar_id">Pilier *</Label>
                  <Select
                    value={formData.pillar_id?.toString()}
                    onValueChange={(value) => setFormData({ ...formData, pillar_id: parseInt(value) })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un pilier" />
                    </SelectTrigger>
                    <SelectContent>
                      {pillars.map((pillar) => (
                        <SelectItem key={pillar.id} value={pillar.id.toString()}>
                          {pillar.icon} {pillar.name || pillar.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="title">Titre du module *</Label>
                  <Input
                    id="title"
                    value={formData.title || ''}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="ex: Clarifier son rôle de manager"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    value={formData.description || ''}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={4}
                    placeholder="Décrivez ce module..."
                    required
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="icon">Icône (emoji)</Label>
                    <Input
                      id="icon"
                      value={formData.icon || ''}
                      onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                      placeholder="📄"
                      maxLength={2}
                    />
                  </div>

                  <div>
                    <Label htmlFor="order">Ordre</Label>
                    <Input
                      id="order"
                      type="number"
                      value={formData.order || 1}
                      onChange={(e) => setFormData({ ...formData, order: e.target.value })}
                      min="1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="duration_minutes">Durée (min)</Label>
                    <Input
                      id="duration_minutes"
                      type="number"
                      value={formData.duration_minutes || 30}
                      onChange={(e) => setFormData({ ...formData, duration_minutes: e.target.value })}
                      min="1"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="is_visible"
                    checked={formData.is_visible !== false}
                    onChange={(e) => setFormData({ ...formData, is_visible: e.target.checked })}
                    className="rounded"
                  />
                  <Label htmlFor="is_visible">Module visible aux utilisateurs</Label>
                </div>
              </>
            )}

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
              >
                Annuler
              </Button>
              <Button type="submit">
                <Save className="w-4 h-4 mr-2" />
                Enregistrer
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ========== PILLARS TAB ==========
const PillarsTab = ({ pillars, onAdd, onEdit, onDelete }) => {
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Les 5 Piliers de la Formation</h2>
        <Button onClick={onAdd} className="gap-2">
          <Plus className="w-4 h-4" />
          Ajouter un pilier
        </Button>
      </div>

      <div className="grid gap-4">
        {pillars
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map((pillar) => (
            <Card key={pillar.id} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    <GripVertical className="w-5 h-5 text-gray-400 mt-1" />
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="text-2xl">{pillar.icon || '📚'}</span>
                        <CardTitle>{pillar.name || pillar.title}</CardTitle>
                        <span className="text-sm text-gray-500">Pilier {pillar.order}/5</span>
                      </div>
                      <CardDescription className="mb-3">{pillar.description}</CardDescription>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span>📖 {pillar.module_count || 0} modules</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onEdit(pillar)}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onDelete(pillar.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
            </Card>
          ))}
      </div>

      {pillars.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <Layers className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">Aucun pilier trouvé</p>
          <Button onClick={onAdd}>
            <Plus className="w-4 h-4 mr-2" />
            Créer le premier pilier
          </Button>
        </div>
      )}
    </div>
  );
};

// ========== MODULES TAB ==========
const ModulesTab = ({ modules, pillars, navigate, onAdd, onEdit, onDelete, onToggleVisibility }) => {
  const [selectedPillar, setSelectedPillar] = useState('all');
  const [audioStatuses, setAudioStatuses] = useState({}); // { moduleId: status }
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [generatingModules, setGeneratingModules] = useState(new Set());

  const filteredModules = selectedPillar === 'all'
    ? modules
    : modules.filter(m => m.pillar_id === parseInt(selectedPillar));

  // Fetch audio statuses for all modules
  const fetchAudioStatuses = async () => {
    try {
      setLoadingAudio(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/modules/audio-automation/check-all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const map = {};
      (res.data.modules || []).forEach(m => {
        map[m.module_id] = m;
      });
      setAudioStatuses(map);
    } catch (err) {
      console.error('Failed to load audio statuses:', err);
    } finally {
      setLoadingAudio(false);
    }
  };

  useEffect(() => {
    if (modules.length > 0) fetchAudioStatuses();
  }, [modules.length]);

  // Generate audio for a single module
  const handleGenerate = async (moduleId) => {
    try {
      setGeneratingModules(prev => new Set(prev).add(String(moduleId)));
      const token = localStorage.getItem('token');
      await axios.post(`${API}/modules/audio-automation/regenerate/${moduleId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Génération audio lancée pour le module ${moduleId}`);
      // Poll status after a delay
      setTimeout(() => fetchAudioStatuses(), 5000);
      setTimeout(() => fetchAudioStatuses(), 15000);
      setTimeout(() => {
        fetchAudioStatuses();
        setGeneratingModules(prev => {
          const next = new Set(prev);
          next.delete(String(moduleId));
          return next;
        });
      }, 30000);
    } catch (err) {
      toast.error('Erreur lors du lancement de la génération audio');
      setGeneratingModules(prev => {
        const next = new Set(prev);
        next.delete(String(moduleId));
        return next;
      });
    }
  };

  // Generate all missing/outdated
  const handleGenerateAll = async () => {
    try {
      setLoadingAudio(true);
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/modules/audio-automation/generate-missing`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const count = res.data?.modules_count || 0;
      if (count > 0) {
        toast.success(`Génération lancée pour ${count} module(s)`);
        setTimeout(() => fetchAudioStatuses(), 10000);
        setTimeout(() => fetchAudioStatuses(), 30000);
      } else {
        toast.info('Tous les audios sont déjà à jour');
      }
    } catch (err) {
      toast.error('Erreur lors de la génération groupée');
    } finally {
      setLoadingAudio(false);
    }
  };

  // Summary counts
  const readyCount = Object.values(audioStatuses).filter(s => s.generation_status === 'ready').length;
  const outdatedCount = Object.values(audioStatuses).filter(s => s.generation_status === 'outdated').length;
  const pendingCount = Object.values(audioStatuses).filter(s => s.generation_status === 'pending').length;
  const generatingCount = Object.values(audioStatuses).filter(s => s.generation_status === 'generating').length;
  const failedCount = Object.values(audioStatuses).filter(s => s.generation_status === 'failed').length;
  const staleCount = Object.values(audioStatuses).filter(s => s.generation_status === 'stale').length;
  const totalModules = Object.keys(audioStatuses).length;

  // Iter338 — Stop a stuck/long-running generation (admin)
  const handleStop = async (moduleId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/admin/audio/cancel/${moduleId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.success) {
        toast.success(`Génération du module ${moduleId} arrêtée`);
      } else {
        toast.info(res.data?.message || 'Aucune génération active à arrêter');
      }
      fetchAudioStatuses();
    } catch (err) {
      toast.error("Erreur lors de l'arrêt de la génération");
    }
  };

  // Iter338 — Retry from stale/failed/error (reuses /admin/audio/retry)
  const handleRetry = async (moduleId) => {
    try {
      setGeneratingModules(prev => new Set(prev).add(String(moduleId)));
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/audio/retry/${moduleId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Module ${moduleId} relancé`);
      // Then trigger an actual generation pass
      await axios.post(`${API}/modules/audio-automation/regenerate/${moduleId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTimeout(() => fetchAudioStatuses(), 5000);
      setTimeout(() => fetchAudioStatuses(), 15000);
      setTimeout(() => {
        fetchAudioStatuses();
        setGeneratingModules(prev => {
          const next = new Set(prev);
          next.delete(String(moduleId));
          return next;
        });
      }, 30000);
    } catch (err) {
      toast.error('Erreur lors de la relance');
      setGeneratingModules(prev => {
        const next = new Set(prev);
        next.delete(String(moduleId));
        return next;
      });
    }
  };

  // Iter338 — Format job duration since lock_acquired_at
  const fmtJobDuration = (lockAcquiredAt) => {
    if (!lockAcquiredAt) return null;
    const start = new Date(lockAcquiredAt).getTime();
    if (Number.isNaN(start)) return null;
    const elapsedSec = Math.max(0, Math.floor((Date.now() - start) / 1000));
    if (elapsedSec < 60) return `${elapsedSec}s`;
    const m = Math.floor(elapsedSec / 60);
    const s = elapsedSec % 60;
    return `${m}min ${s.toString().padStart(2, '0')}s`;
  };

  // Helper: format duration
  const fmtDuration = (s) => {
    if (!s) return '--';
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  // Helper: format file size
  const fmtSize = (bytes) => {
    if (!bytes) return '--';
    return `${(bytes / 1024 / 1024).toFixed(1)} Mo`;
  };

  // Helper: audio status badge
  const AudioBadge = ({ status }) => {
    const cfg = {
      ready:      { icon: CheckCircle2, label: 'Prêt',        cls: 'bg-green-50 text-green-700 border-green-200' },
      generating: { icon: Loader2,      label: 'En cours',    cls: 'bg-blue-50 text-blue-700 border-blue-200' },
      outdated:   { icon: AlertCircle,  label: 'Obsolète',    cls: 'bg-amber-50 text-amber-700 border-amber-200' },
      pending:    { icon: Clock,        label: 'En attente',  cls: 'bg-gray-50 text-gray-600 border-gray-200' },
      failed:     { icon: AlertCircle,  label: 'Échec',       cls: 'bg-red-50 text-red-700 border-red-200' },
      stale:      { icon: AlertCircle,  label: 'Expiré',      cls: 'bg-orange-50 text-orange-700 border-orange-200' },
    };
    const c = cfg[status] || cfg.pending;
    const Icon = c.icon;
    const spinning = status === 'generating';
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium border rounded-full ${c.cls}`} data-testid={`audio-badge-${status}`}>
        <Icon className={`w-3 h-3 ${spinning ? 'animate-spin' : ''}`} />
        {c.label}
      </span>
    );
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Modules de Formation</h2>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
          <Select value={selectedPillar} onValueChange={setSelectedPillar}>
            <SelectTrigger className="w-full sm:w-[250px]" data-testid="pillar-filter">
              <SelectValue placeholder="Filtrer par pilier" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les piliers</SelectItem>
              {pillars.map(pillar => (
                <SelectItem key={pillar.id} value={pillar.id.toString()}>
                  {pillar.icon} {pillar.name || pillar.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={onAdd} className="gap-2 w-full sm:w-auto" data-testid="add-module-btn">
            <Plus className="w-4 h-4" />
            Ajouter un module
          </Button>
        </div>
      </div>

      {/* Audio Dashboard Summary */}
      {totalModules > 0 && (
        <Card className="mb-6 border-violet-200 bg-gradient-to-r from-violet-50 to-white" data-testid="audio-dashboard-summary">
          <CardContent className="py-3 sm:py-4 px-4 sm:px-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6">
                <div className="flex items-center gap-2">
                  <Volume2 className="w-5 h-5 text-violet-600" />
                  <span className="text-sm font-semibold text-violet-900">Gestion Audio</span>
                </div>
                <div className="flex flex-wrap items-center gap-3 text-xs">
                  <span className="flex items-center gap-1 text-green-700">
                    <CheckCircle2 className="w-3.5 h-3.5" /> {readyCount} prêts
                  </span>
                  {outdatedCount > 0 && (
                    <span className="flex items-center gap-1 text-amber-700">
                      <AlertCircle className="w-3.5 h-3.5" /> {outdatedCount} obsolètes
                    </span>
                  )}
                  {pendingCount > 0 && (
                    <span className="flex items-center gap-1 text-gray-600">
                      <Clock className="w-3.5 h-3.5" /> {pendingCount} en attente
                    </span>
                  )}
                  {generatingCount > 0 && (
                    <span className="flex items-center gap-1 text-blue-700">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> {generatingCount} en cours
                    </span>
                  )}
                  {staleCount > 0 && (
                    <span className="flex items-center gap-1 text-orange-700" data-testid="audio-stale-count">
                      <AlertCircle className="w-3.5 h-3.5" /> {staleCount} expirés
                    </span>
                  )}
                  {failedCount > 0 && (
                    <span className="flex items-center gap-1 text-red-700">
                      <AlertCircle className="w-3.5 h-3.5" /> {failedCount} en échec
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 self-end sm:self-auto">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={fetchAudioStatuses}
                  disabled={loadingAudio}
                  className="gap-1 text-xs"
                  data-testid="audio-refresh-btn"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${loadingAudio ? 'animate-spin' : ''}`} />
                  Actualiser
                </Button>
                {(outdatedCount > 0 || pendingCount > 0) && (
                  <Button
                    size="sm"
                    onClick={handleGenerateAll}
                    disabled={loadingAudio}
                    className="gap-1 text-xs bg-violet-600 hover:bg-violet-700"
                    data-testid="audio-generate-all-btn"
                  >
                    <Volume2 className="w-3.5 h-3.5" />
                    Générer tout ({outdatedCount + pendingCount})
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Module Cards */}
      <div className="grid gap-4">
        {filteredModules
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map((module) => {
            const pillar = pillars.find(p => p.id === module.pillar_id);
            const audio = audioStatuses[String(module.id)];
            const isGen = generatingModules.has(String(module.id)) || audio?.generation_status === 'generating';
            
            return (
              <Card 
                key={module.id} 
                className={`hover:shadow-md transition-all ${
                  module.is_visible !== false ? '' : 'opacity-60 bg-gray-50'
                }`}
              >
                <CardHeader className="pb-3 px-4 sm:px-6">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="text-xl sm:text-2xl">{module.icon || '📄'}</span>
                        <CardTitle className="text-sm sm:text-base">{module.title}</CardTitle>
                        {module.is_visible === false && (
                          <span className="px-2 py-0.5 text-xs bg-gray-200 text-gray-700 rounded-full">
                            Masqué
                          </span>
                        )}
                      </div>
                      <CardDescription className="mb-2 line-clamp-2 text-xs sm:text-sm">{module.description}</CardDescription>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-gray-500">
                        <span>📚 {pillar?.name || pillar?.title || 'Sans pilier'}</span>
                        <span className="hidden sm:inline">•</span>
                        <span>Module {module.order}</span>
                        {module.duration_minutes && (
                          <>
                            <span className="hidden sm:inline">•</span>
                            <span>⏱️ {module.duration_minutes} min</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0 self-start sm:self-auto flex-wrap">
                      <Button
                        size="sm"
                        variant="default"
                        onClick={() => navigate(`/admin/modules/${module.id}/content`)}
                        className="gap-1 text-xs h-8"
                        data-testid={`module-content-${module.id}`}
                      >
                        <FileText className="w-3 h-3" />
                        Contenu
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onToggleVisibility(module.id, module.is_visible !== false)}
                        title={module.is_visible !== false ? 'Masquer' : 'Afficher'}
                        className="h-8 w-8 p-0"
                        data-testid={`module-visibility-${module.id}`}
                      >
                        {module.is_visible !== false ? (
                          <Eye className="w-4 h-4 text-green-600" />
                        ) : (
                          <EyeOff className="w-4 h-4 text-gray-400" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onEdit(module)}
                        className="h-8 w-8 p-0"
                        data-testid={`module-edit-${module.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onDelete(module.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                        data-testid={`module-delete-${module.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>

                {/* Audio Control Row */}
                {audio && (
                  <div className="px-4 sm:px-6 pb-4 pt-0">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-3 sm:px-4 py-2.5 rounded-lg bg-gray-50 border border-gray-100" data-testid={`audio-row-${module.id}`}>
                      <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                        <Volume2 className="w-4 h-4 text-gray-400 hidden sm:block" />
                        <AudioBadge status={isGen ? 'generating' : audio.generation_status} />
                        {/* Iter338 — Job duration since lock_acquired_at (only when generating) */}
                        {audio.generation_status === 'generating' && audio.lock_acquired_at && (
                          <span className="text-xs text-blue-700 font-medium" data-testid={`audio-job-duration-${module.id}`}>
                            ⏱ {fmtJobDuration(audio.lock_acquired_at)}
                          </span>
                        )}
                        {audio.duration_seconds > 0 && (
                          <span className="text-xs text-gray-500">
                            {fmtDuration(audio.duration_seconds)}
                          </span>
                        )}
                        {audio.file_size_bytes > 0 && (
                          <span className="text-xs text-gray-400">
                            {fmtSize(audio.file_size_bytes)}
                          </span>
                        )}
                        {audio.last_generated_at && (
                          <span className="text-xs text-gray-400">
                            {new Date(audio.last_generated_at).toLocaleDateString('fr-FR')}
                          </span>
                        )}
                        {/* Iter338 — Real error message displayed inline */}
                        {(audio.generation_status === 'stale' || audio.generation_status === 'failed') && audio.error_user_message && (
                          <span className="text-xs text-red-600 italic" data-testid={`audio-error-${module.id}`}>
                            ⚠ {audio.error_user_message}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 self-end sm:self-auto">
                        {/* Iter338 — Stop button when generating */}
                        {audio.generation_status === 'generating' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleStop(module.id)}
                            className="gap-1 text-xs h-7 text-red-700 hover:bg-red-50 border-red-200"
                            data-testid={`audio-stop-${module.id}`}
                          >
                            <AlertCircle className="w-3 h-3" />
                            Arrêter
                          </Button>
                        )}
                        {/* Iter338 — Retry button for stale / failed / pending / outdated */}
                        {(audio.generation_status === 'stale' || audio.generation_status === 'failed') && (
                          <Button
                            size="sm"
                            onClick={() => handleRetry(module.id)}
                            disabled={isGen}
                            className="gap-1 text-xs h-7 bg-violet-600 hover:bg-violet-700"
                            data-testid={`audio-retry-${module.id}`}
                          >
                            {isGen ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <RefreshCw className="w-3 h-3" />
                            )}
                            Relancer
                          </Button>
                        )}
                        {(audio.generation_status === 'ready' || audio.generation_status === 'outdated') && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleGenerate(module.id)}
                            disabled={isGen}
                            className="gap-1 text-xs h-7"
                            data-testid={`audio-regen-${module.id}`}
                          >
                            {isGen ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <RefreshCw className="w-3 h-3" />
                            )}
                            {audio.generation_status === 'outdated' ? 'Mettre à jour' : 'Régénérer'}
                          </Button>
                        )}
                        {audio.generation_status === 'pending' && (
                          <Button
                            size="sm"
                            onClick={() => handleGenerate(module.id)}
                            disabled={isGen}
                            className="gap-1 text-xs h-7 bg-violet-600 hover:bg-violet-700"
                            data-testid={`audio-gen-${module.id}`}
                          >
                            {isGen ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Volume2 className="w-3 h-3" />
                            )}
                            Générer
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
      </div>

      {filteredModules.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">Aucun module trouvé</p>
          <Button onClick={onAdd}>
            <Plus className="w-4 h-4 mr-2" />
            Créer le premier module
          </Button>
        </div>
      )}
    </div>
  );
};

export default AdminModules;
